import React, { useState, useMemo } from 'react';
import { useStore } from '../../store';
import { Plus, Play, CheckCircle, Trash2, ChevronDown, ChevronRight, Calendar, Target } from 'lucide-react';
import { STATUS, PRIORITY, TASK_TYPE, fmt } from '../../utils';
import { AvatarGroup } from '../UI/Avatar';
import CreateTaskModal from './CreateTaskModal';
import { usePermissions } from '../../hooks/usePermissions';

function SprintBlock({ sprint, tasks, onStart, onComplete, onDelete, onOpenTask, onAddTask }) {
  const [open, setOpen] = useState(sprint.status === 'active' || sprint.status === 'planning');
  const done = tasks.filter(t => t.status === 'done').length;
  const { canCreateTask } = usePermissions();
  const canCreate = canCreateTask(activeProject);
  const progress = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  const statusColors = {
    planning: 'text-gray-500 bg-gray-100 dark:bg-gray-800',
    active: 'text-green-600 bg-green-50 dark:bg-green-900/30',
    completed: 'text-gray-400 bg-gray-50 dark:bg-gray-800',
  };
  const statusLabels = { planning: 'Планирование', active: 'Активный', completed: 'Завершён' };

  return (
    <div className={`card mb-4 overflow-hidden ${sprint.status === 'completed' ? 'opacity-60' : ''}`}>
      {/* Sprint header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
        <button onClick={() => setOpen(o => !o)} className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="font-semibold text-sm">{sprint.name}</h3>
            <span className={`tag text-[11px] ${statusColors[sprint.status]}`}>{statusLabels[sprint.status]}</span>
            <span className="text-xs text-gray-400">{tasks.length} задач · {done} готово</span>
          </div>
          {sprint.goal && <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><Target className="w-3 h-3" />{sprint.goal}</p>}
          {(sprint.startDate || sprint.endDate) && (
            <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
              <Calendar className="w-3 h-3" />
              {sprint.startDate ? fmt.dateShort(sprint.startDate) : '?'} → {sprint.endDate ? fmt.dateShort(sprint.endDate) : '?'}
            </p>
          )}
        </div>

        {/* Progress bar */}
        {tasks.length > 0 && (
          <div className="w-24 hidden sm:block">
            <div className="flex justify-between text-[10px] text-gray-400 mb-1">
              <span>{progress}%</span>
              <span>{done}/{tasks.length}</span>
            </div>
            <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-brand-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1">
          {sprint.status === 'planning' && (
            <button onClick={() => onStart(sprint._id)} className="btn-primary py-1 px-2 text-xs">
              <Play className="w-3 h-3" /> Начать
            </button>
          )}
          {sprint.status === 'active' && (
            <button onClick={() => onComplete(sprint._id)} className="btn-secondary py-1 px-2 text-xs">
              <CheckCircle className="w-3 h-3" /> Завершить
            </button>
          )}
          <button onClick={() => onAddTask(sprint._id)} className="btn-ghost py-1 px-2 text-xs">
            <Plus className="w-3 h-3" />
          </button>
          {sprint.status !== 'active' && (
            <button onClick={() => onDelete(sprint._id)} className="btn-ghost py-1 px-1 text-red-400 hover:text-red-500">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Tasks */}
      {open && (
        <div>
          {tasks.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400">
              Задач нет ·

              <button onClick={() => onAddTask(sprint._id)} className="text-brand-600 hover:underline">добавить</button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
                {tasks.map(task => {
                  const s = STATUS[task.status];
                  const p = PRIORITY[task.priority];
                  const due = fmt.dueBadge(task.dueDate);
                  return (
                    <tr key={task._id} onClick={() => onOpenTask(task._id)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span>{TASK_TYPE[task.type]?.icon}</span>
                          <span className="font-medium hover:text-brand-600 dark:hover:text-brand-400 transition-colors">{task.title}</span>
                          {task.number && <span className="text-xs text-gray-400 font-mono">#{task.number}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 w-32">
                        <span className="tag text-xs" style={{ background: s?.color + '20', color: s?.color }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: s?.color }} />
                          {s?.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 w-10 text-sm">{p?.icon}</td>
                      <td className="px-3 py-2.5 w-28">
                        {due && <span className={`tag text-[11px] ${due.cls}`}>{due.label}</span>}
                      </td>
                      <td className="px-3 py-2.5 w-20">
                        {task.assignees?.length > 0 && <AvatarGroup users={task.assignees} max={2} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function SprintView() {
  const { sprints, tasks, activeProject, createSprint, startSprint, completeSprint, deleteSprint, openTask } = useStore();
  const [showNewSprint, setShowNewSprint] = useState(false);
  const { canCreateTask } = usePermissions();
  const canCreate = canCreateTask(activeProject);
  console.log('SprintView render', canCreate);
  const [newSprintName, setNewSprintName] = useState('');
  const [newSprintGoal, setNewSprintGoal] = useState('');
  const [addTaskSprint, setAddTaskSprint] = useState(null);

  const tasksBySprint = useMemo(() => {
    const map = { backlog: [] };
    sprints.forEach(s => { map[s._id] = []; });
    tasks.filter(t => !t.parent).forEach(t => {
      const key = t.sprint || 'backlog';
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [tasks, sprints]);

  const handleCreateSprint = async () => {
    if (!newSprintName.trim()) return;
    const count = sprints.length + 1;
    await createSprint({
      name: newSprintName || `Спринт ${count}`,
      goal: newSprintGoal,
      project: activeProject._id,
    });
    setNewSprintName('');
    setNewSprintGoal('');
    setShowNewSprint(false);
  };

  const handleStart = async (id) => {
    if (window.confirm('Начать спринт? Активный спринт (если есть) будет завершён.')) {
      await startSprint(id, { project: activeProject._id });
    }
  };

  const handleComplete = async (id) => {
    if (window.confirm('Завершить спринт? Незакрытые задачи вернутся в бэклог.')) {
      await completeSprint(id, { project: activeProject._id });
    }
  };

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold">Спринты</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{sprints.filter(s => s.status === 'active').length > 0 ? '🟢 Спринт активен' : '⚪ Нет активного спринта'}</p>
          </div>
          {canCreate && (
            <button onClick={() => setShowNewSprint(true)} className="btn-primary py-1.5">
              <Plus className="w-4 h-4" /> Создать спринт
            </button>
          )}
          {!canCreate && (
            <button disabled title="Требуется роль участника или выше" onClick={() => setShowNewSprint(true)} className="btn-primary py-1.5">
              <Plus className="w-4 h-4" /> Создать спринт
            </button>
          )}
        </div>

        {/* New sprint form */}
        {showNewSprint && (
          <div className="card p-4 mb-4 animate-slide-in">
            <h3 className="font-medium text-sm mb-3">Новый спринт</h3>
            <div className="space-y-2">
              <input className="input text-sm" placeholder={`Спринт ${sprints.length + 1}`}
                value={newSprintName} onChange={e => setNewSprintName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateSprint()} autoFocus />
              <input className="input text-sm" placeholder="Цель спринта (опционально)"
                value={newSprintGoal} onChange={e => setNewSprintGoal(e.target.value)} />
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setShowNewSprint(false)} className="btn-secondary text-sm flex-1 justify-center">Отмена</button>
              <button onClick={handleCreateSprint} className="btn-primary text-sm flex-1 justify-center">Создать</button>
            </div>
          </div>
        )}

        {/* Active / planning sprints */}
        {sprints.filter(s => s.status !== 'completed').map(sprint => (
          <SprintBlock key={sprint._id} sprint={sprint}
            tasks={tasksBySprint[sprint._id] || []}
            onStart={handleStart}
            onComplete={handleComplete}
            onDelete={deleteSprint}
            onOpenTask={openTask}
            onAddTask={(sid) => setAddTaskSprint(sid)}
          />
        ))}

        {/* Backlog */}
        <div className="card overflow-hidden mb-4">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <h3 className="font-semibold text-sm">Бэклог</h3>
            <span className="text-xs text-gray-400">{tasksBySprint.backlog?.length || 0} задач</span>
            <div className="flex-1" />
            {canCreate && (
            <button onClick={() => setAddTaskSprint('backlog')} className="btn-ghost py-1 px-2 text-xs">
              <Plus className="w-3 h-3" /> Добавить
            </button>
            )}
          </div>
          {(tasksBySprint.backlog || []).length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400">Бэклог пуст</div>
          ) : (
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
                {(tasksBySprint.backlog || []).map(task => {
                  const s = STATUS[task.status];
                  const p = PRIORITY[task.priority];
                  return (
                    <tr key={task._id} onClick={() => openTask(task._id)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span>{TASK_TYPE[task.type]?.icon}</span>
                          <span className="font-medium">{task.title}</span>
                          {task.number && <span className="text-xs text-gray-400 font-mono">#{task.number}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 w-32">
                        <span className="tag text-xs" style={{ background: s?.color + '20', color: s?.color }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: s?.color }} />
                          {s?.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 w-10">{p?.icon}</td>
                      <td className="px-3 py-2.5 w-28">
                        {task.dueDate && <span className="text-xs text-gray-400">{fmt.dateShort(task.dueDate)}</span>}
                      </td>
                      <td className="px-3 py-2.5 w-20">
                        {task.assignees?.length > 0 && <AvatarGroup users={task.assignees} max={2} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Completed sprints */}
        {sprints.filter(s => s.status === 'completed').length > 0 && (
          <details className="mt-2">
            <summary className="text-sm text-gray-400 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300 mb-3">
              Завершённые спринты ({sprints.filter(s => s.status === 'completed').length})
            </summary>
            {sprints.filter(s => s.status === 'completed').map(sprint => (
              <SprintBlock key={sprint._id} sprint={sprint}
                tasks={tasksBySprint[sprint._id] || []}
                onStart={handleStart} onComplete={handleComplete}
                onDelete={deleteSprint} onOpenTask={openTask}
                onAddTask={() => { }} />
            ))}
          </details>
        )}
      </div>

      {addTaskSprint && (
        <CreateTaskModal
          onClose={() => setAddTaskSprint(null)}
          defaultStatus={addTaskSprint}
        />
      )}
    </div>
  );
}
